import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { T } from '@transifex/react'
import { createPortal } from 'react-dom'
import { ApiProduct } from '@vend/types'
import { getSelectedProduct } from '@vend/business-components'
import { WrReactProductSearch } from '../product/WrReactProductSearch'
import {
  EnhancedProductSuggestion,
  ProductDetails,
} from '../AddProductToSale/useAddProductToSale'
import { useProductBarcodeSearch } from '../product/useProductBarcodeSearch'
import { useProductsStore, useProductSuggestions } from '../../ng-compat/hooks'
import { NewVariantDetails } from '../AddProductToSale/useAddProductToSaleStore'
import {
  VariantPickerModal,
  VariantPickerModalProps,
} from '../product/variant-picker-modal/VariantPickerModal'
import { findFolderInUpdatedLayout } from './utils/qk-layout'
import { QuickKeyNodeType } from './utils/qk.constants'
import {
  ApiNode,
  QuickKey,
  QuickKeyBlank,
  QuickKeyFolder,
  QuickKeyKey,
  QuickKeyRoot,
} from './utils/qk-types'
import { QKRoot } from './nodes/QKRoot'
import { QuickKeysKeyboardNav } from './QuickKeysKeyboardNav'
import {
  AddProductParams,
  getFirstBlank,
  getFirstChild,
  HandleDropParams,
  isValidDropDestination,
} from './quickKeyUtils'
import { EditQuickKeyModal } from './EditQuickKeyModal'

interface QuickKeysWorkspaceProps {
  layout: QuickKeyRoot
  addProduct: ({
    product,
    node,
    isChild,
    currentRoot,
  }: AddProductParams) => void
  setNode: (node: ApiNode) => void
  addNodeToFolder: (node: ApiNode, folder: ApiNode) => Promise<void>
  combineNodesToFolder: (target: ApiNode, dropped: ApiNode) => Promise<void>
  deleteNode: (node: ApiNode) => void
}

export const QuickKeysWorkspace: FunctionComponent<QuickKeysWorkspaceProps> = ({
  layout,
  addProduct,
  setNode,
  addNodeToFolder,
  combineNodesToFolder,
  deleteNode,
}) => {
  const productBarcodeSearch = useProductBarcodeSearch()
  const productsStore = useProductsStore()
  const productSuggestions = useProductSuggestions()
  const [currentRoot, setCurrentRoot] = useState<QuickKeyFolder | null>(null)
  const [focussed, setFocussed] = useState<
    QuickKeyFolder | QuickKeyKey | QuickKeyBlank | null
  >(null)
  const [selectedBlank, setSelectedBlank] = useState<QuickKeyBlank | null>(null)
  const [editQk, setEditQk] = useState<QuickKeyKey | QuickKeyFolder | null>(
    null
  )
  const [
    variantPickerOptions,
    setVariantPickerOptions,
  ] = useState<VariantPickerModalProps | null>(null)

  useEffect(() => {
    if (currentRoot) {
      const updatedFolder = findFolderInUpdatedLayout(
        currentRoot,
        layout.node.id,
        layout
      )

      setCurrentRoot(updatedFolder)
    }
  }, [currentRoot, layout])

  const selectQuickKey = (quickKey: QuickKey | null) => {
    if (!quickKey) {
      return
    }
    switch (quickKey?.type) {
      case QuickKeyNodeType.BLANK:
        setSelectedBlank(quickKey)
        return
      case QuickKeyNodeType.FOLDER:
        setCurrentRoot(quickKey)
        setFocussed(getFirstChild(quickKey) || null)
        return
      case QuickKeyNodeType.QUICK_KEY:
        setEditQk(quickKey)
        return
      default:
        return
    }
  }

  const resolveVariant = async (
    product: ApiProduct
  ): Promise<ProductDetails | null> => {
    const variantProducts = await productsStore.getAllVariantProducts(product)
    const result = await new Promise<ProductDetails | null>(resolve => {
      const close = async (
        variantPromise?: Promise<
          ApiProduct | ApiProduct[] | NewVariantDetails | undefined
        >
      ) => {
        const result = await variantPromise
        if (!result) {
          resolve(null)
          setVariantPickerOptions(null)
          return
        }

        if ('hasNewVariantAttribute' in result) {
          // New variant created which we've disabled in the picker options
          // so this never happens
          resolve(null)
          setVariantPickerOptions(null)
          return
        }
        const selectedProduct = getSelectedProduct(result)

        // Because type of product/variant are both defined as {}
        // despite one of them being populated depending on value
        // of exactMatch
        resolve({
          label: selectedProduct.exactMatch
            ? // @ts-expect-error
              selectedProduct.variant.variant_name
            : // @ts-expect-error
              selectedProduct.product.name,

          productId: selectedProduct.id,
          isChild: selectedProduct.exactMatch,
        })
        setVariantPickerOptions(null)
      }

      setVariantPickerOptions({
        close,
        options: {
          title: 'What would you like to set as this Quick Key?',
          parentProduct: product,
          variantProducts,
          allowVariantParentSelect: true,
          showAddValue: false,
        },
      })
    })

    return result
  }

  const handleProductSelect = async (product: ProductDetails | null) => {
    if (!product) {
      return
    }

    const enhancedProduct = (await productSuggestions.enhance({
      id: product.productId,
      exactMatch: product.isChild,
    })) as EnhancedProductSuggestion

    if (!enhancedProduct.product) {
      return
    }

    let actualProduct: ProductDetails | null = product
    if (enhancedProduct.variantCount && !enhancedProduct.exactMatch) {
      actualProduct = await resolveVariant(enhancedProduct.product)
    }

    if (!actualProduct) {
      return
    }

    if (selectedBlank) {
      addProduct({
        product: actualProduct,
        node: selectedBlank.node,
        isChild: actualProduct.isChild,
        currentRoot: currentRoot ? currentRoot.node : null,
      })
      setSelectedBlank(null)
      return
    }

    const firstBlank = getFirstBlank(currentRoot || layout)

    if (!firstBlank) {
      // Should always be able to find a blank
      throw new Error('No blank quick key available to add product to')
    }

    addProduct({
      product: actualProduct,
      node: firstBlank.node,
      isChild: actualProduct.isChild,
      currentRoot: currentRoot ? currentRoot.node : null,
    })
  }

  const updateNode = useCallback(
    (node: ApiNode) => {
      setNode(node)
      setEditQk(null)
    },
    [setNode]
  )

  const deleteQuickKeyAndClose = useCallback(
    (node: ApiNode) => {
      deleteNode(node)
      setEditQk(null)
      if (currentRoot && node.id === currentRoot.node.id) {
        setCurrentRoot(null)
      }
    },
    [currentRoot, deleteNode]
  )

  const handleDrop = useCallback(
    async ({ dropped, target }: HandleDropParams) => {
      if (dropped.node.id === target.node.id) {
        return
      }
      if (!isValidDropDestination(target, dropped)) {
        return
      }

      if (dropped.type === QuickKeyNodeType.QUICK_KEY) {
        switch (target.type) {
          case QuickKeyNodeType.FOLDER:
            await addNodeToFolder(target.node, dropped.node)
            return
          case QuickKeyNodeType.QUICK_KEY: {
            await combineNodesToFolder(target.node, dropped.node)
            return
          }
          case QuickKeyNodeType.BLANK: {
            const updatedNode = {
              ...dropped.node,
              position: target.node.position,
              parent_node_id: target.node.parent_node_id,
            }
            setNode(updatedNode)
            return
          }
        }
      }

      if (
        dropped.type === QuickKeyNodeType.FOLDER &&
        target.type === QuickKeyNodeType.BLANK
      ) {
        const updatedNode = {
          ...dropped.node,
          position: target.node.position,
          parent_node_id: target.node.parent_node_id,
        }
        setNode(updatedNode)
        return
      }
    },
    [addNodeToFolder, combineNodesToFolder, setNode]
  )

  const feelingLucky = async (query: string, suggestionsAvailable: boolean) => {
    const result = await productBarcodeSearch(query)

    return { handled: false, product: result?.product }
  }

  return (
    <>
      <div className="edit-qk-layout-workspace">
        <label className="vd-label">
          <div className="vd-mb1">
            <T _str="Search for products" />
          </div>
          <WrReactProductSearch
            onProductSelected={handleProductSelect}
            feelingLucky={feelingLucky}
          />
        </label>
        <QuickKeysKeyboardNav
          layout={layout}
          includeBlanks={true}
          currentRoot={currentRoot}
          focussed={focussed}
          setFocussed={setFocussed}
          setCurrentRoot={setCurrentRoot}
          selectQuickKey={selectQuickKey}
          className="edit-qk-layout-panel-layout vd-quick-keys--edit-mode"
        >
          <QKRoot
            setRoot={setCurrentRoot}
            setFocus={setFocussed}
            focussed={focussed}
            selectedBlank={selectedBlank}
            quickKey={currentRoot || layout}
            selectQuickKey={selectQuickKey}
            editFolder={setEditQk}
            handleDrop={handleDrop}
          />
        </QuickKeysKeyboardNav>
      </div>
      {editQk
        ? createPortal(
            <EditQuickKeyModal
              close={() => setEditQk(null)}
              quickKey={editQk}
              updateNode={updateNode}
              deleteNode={deleteQuickKeyAndClose}
            />,
            document.body
          )
        : null}
      {variantPickerOptions
        ? createPortal(
            <VariantPickerModal {...variantPickerOptions} />,
            document.body
          )
        : null}
    </>
  )
}
